/**
 * src/components/analytics/LandingTracker.jsx
 *
 * Componente client-side que trackea:
 *   1. Page views automáticamente al montar
 *   2. CTA clicks vía data-track attributes en los elementos
 *   3. Scroll depth (secciones visibles)
 *
 * Uso en una página pública:
 *   import LandingTracker from '@/components/analytics/LandingTracker';
 *   // Dentro del JSX:
 *   <LandingTracker />
 *
 * Para trackear clicks en CTAs, agregar data-track al elemento:
 *   <Link href="/planes" data-track="cta-ver-planes">Ver Planes</Link>
 *   <Link href="/planesbeta" data-track="cta-demo">Comenzar Demo</Link>
 *
 * El tracker NO bloquea el render, NO usa cookies, NO recopila PII.
 * El session hash es un random generado en sessionStorage (muere al cerrar tab).
 */

'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// ─── Helpers ────────────────────────────────────────────────────

/** Hash anónimo de sesión — muere cuando cierra la tab (sessionStorage). */
function getSessionHash() {
  if (typeof window === 'undefined') return null;
  const KEY = 'dvl_anon_session';
  let hash = sessionStorage.getItem(KEY);
  if (!hash) {
    // Generar un random hex de 16 chars — no es PII, es solo para
    // aproximar "visitantes únicos en una sesión de navegación".
    const arr = new Uint8Array(8);
    crypto.getRandomValues(arr);
    hash = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(KEY, hash);
  }
  return hash;
}

/** Extraer UTM params de la URL actual. */
function getUtmParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    source:   params.get('utm_source')   || undefined,
    medium:   params.get('utm_medium')   || undefined,
    campaign: params.get('utm_campaign') || undefined,
  };
}

/** Enviar evento al API — fire-and-forget (beacon o fetch). */
function sendEvent(event, extra = {}) {
  const payload = {
    event,
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    utm: getUtmParams(),
    sessionHash: getSessionHash(),
    ...extra,
  };

  const body = JSON.stringify(payload);

  // Preferir sendBeacon (no bloquea unload), fallback a fetch
  if (navigator.sendBeacon) {
    navigator.sendBeacon('/api/analytics/track', new Blob([body], { type: 'application/json' }));
  } else {
    fetch('/api/analytics/track', {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => { /* silently ignore */ });
  }
}

// ─── Component ──────────────────────────────────────────────────

export default function LandingTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const trackedRef = useRef(new Set());

  // 1. Track page view on route change
  useEffect(() => {
    // Dedup: solo una vez por pathname en esta sesión del componente
    const key = pathname + (searchParams?.toString() || '');
    if (trackedRef.current.has(key)) return;
    trackedRef.current.add(key);

    // Delay mínimo para no competir con el hydration
    const timer = setTimeout(() => sendEvent('page_view'), 100);
    return () => clearTimeout(timer);
  }, [pathname, searchParams]);

  // 2. Track CTA clicks via event delegation (data-track attribute)
  useEffect(() => {
    function handleClick(e) {
      // Subir hasta encontrar el elemento con data-track (max 5 levels)
      let el = e.target;
      for (let i = 0; i < 5 && el; i++) {
        const trackId = el.getAttribute?.('data-track');
        if (trackId) {
          sendEvent('cta_click', { element: trackId });
          return;
        }
        el = el.parentElement;
      }
    }

    document.addEventListener('click', handleClick, { passive: true });
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // 3. Track scroll sections via IntersectionObserver
  useEffect(() => {
    const observed = new Set();
    const sections = document.querySelectorAll('section[id]');
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !observed.has(entry.target.id)) {
            observed.add(entry.target.id);
            sendEvent('scroll_section', { element: entry.target.id });
          }
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [pathname]);

  // Este componente no renderiza nada
  return null;
}

// ─── Export para uso manual ─────────────────────────────────────

/**
 * Para casos donde necesitás trackear algo programáticamente:
 *   import { trackEvent } from '@/components/analytics/LandingTracker';
 *   trackEvent('plan_select', { element: 'plan-profesional' });
 */
export { sendEvent as trackEvent };
